<template>
  <v-dialog
    persistent
    max-width="600px"
    v-model="conflict">
    <v-card class="dialog-card">
      <ModalHeader
        icon="mdi-alert"
        modalTitle="Resolve Conflicts"
        helpFileName="ResolveConflicts"
        :modalSubtitle="conflict_files.length + ' file(s) have conflicts'"
        @close="
          conflict = false;
          conflict_files = [];
        " />
      <div class="modal-body mx-3">
        <div style="height: 400px; overflow-y: auto; width: 100%">
          <v-card
            variant="flat"
            class="mb-2"
            v-for="(conflict, i) in conflict_files"
            :key="conflict">
            <v-card-title
              class="pl-0"
              style="display: flex">
              <v-btn
                color="primary"
                variant="flat"
                @click="
                  conflict.conflict_handle = 'upgrade';
                  state.upload_files.push(conflict);
                  conflict_files = conflict_files.filter(
                    (e) => e.name !== conflict.name
                  );
                ">
                <v-icon class="ml-1 mr-2">mdi-swap-horizontal</v-icon>
              </v-btn>
              <v-btn
                @click="
                  conflict.conflict_handle = 'rename';
                  state.upload_files.push(conflict);
                  conflict_files = conflict_files.filter(
                    (e) => e.name !== conflict.name
                  );
                "
                class="ml-2 mr-3"
                color="primary"
                variant="flat">
                <v-icon class="ml-1 mr-2">mdi-content-duplicate</v-icon>
              </v-btn>
              {{ conflict.name }}
            </v-card-title>
          </v-card>
        </div>
      </div>
      <div class="modal-footer">
        <v-btn
          @click="upgrade_all()"
          color="primary"
          variant="flat">
          <v-icon class="ml-1 mr-2">mdi-swap-horizontal</v-icon>
          Replace Conflicts
        </v-btn>
        <v-spacer />
        <v-btn
          class="ml-2"
          @click="replace_all()"
          color="primary"
          variant="flat">
          <v-icon class="ml-1 mr-2">mdi-content-duplicate</v-icon>
          Rename Conflicts
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>
<script setup>
import { ref, watch } from 'vue';
import { $http } from './stores/http.js';
import { deluge } from '@/stores/deluge.js';
import { state, IS_CLOUD_MODE } from './stores/deluge.js';
import { generateThumbnail } from './utils/thumbnailGenerator.js';
import ModalHeader from './components/Shared/Modals/ModalHeader.vue';
const file = ref(null);
const file_upload_status = ref({});
const file_type = ref('');
const conflict = ref(false);
const upload_queue = ref([]);
const conflict_files = ref([]);

// Format seconds into human-readable time (e.g., "2m 30s", "1h 5m")
function formatETA(seconds) {
  if (!seconds || seconds <= 0 || !isFinite(seconds)) return '';
  seconds = Math.round(seconds);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// Update ETA based on elapsed time and bytes uploaded
function updateUploadETA(bytesUploaded, totalBytes) {
  state.upload_bytes_done = bytesUploaded;
  const elapsed = (Date.now() - state.upload_start_time) / 1000; // seconds
  if (elapsed > 0.5 && bytesUploaded > 0) {
    const speed = bytesUploaded / elapsed; // bytes per second
    const remaining = totalBytes - bytesUploaded;
    const etaSeconds = remaining / speed;
    state.upload_eta = formatETA(etaSeconds);
  }
}

// Cancel the current upload
async function cancelUpload() {
  state.upload_cancel_requested = true;

  // Cancel the current session on the backend
  if (state.upload_session_uid) {
    try {
      await $http(
        'DELETE',
        '/upload/cancel_upload?uid=' + state.upload_session_uid,
        null,
        10000,
        true
      );
    } catch (e) {
      console.log('Failed to cancel upload on server:', e);
    }
  }

  // Clear the upload queue
  upload_queue.value = [];

  // Reset all upload state
  resetUploadState();
}

// Reset upload state after completion or cancellation
function resetUploadState() {
  state.uploading = false;
  state.upload_current_index = 0;
  state.upload_total = 0;
  state.upload_progress = 0;
  state.upload_eta = '';
  state.upload_session_uid = null;
  state.upload_cancel_requested = false;
  state.upload_current_filename = '';
}

// Expose cancelUpload globally so BottomBar can call it
window.cancelUpload = cancelUpload;
watch(state.upload_files, () => {
  if (conflict_files.value.length > 0) {
    conflict.value = true;
  } else {
    conflict.value = false;
  }
  while (state.upload_files.length > 0) {
    state.upload_total += 1;
    upload_queue.value.push(state.upload_files.shift());
  }
  upload_all_files();
});

function upgrade_all() {
  for (const conflict of conflict_files.value) {
    conflict.conflict_handle = 'upgrade';
    state.upload_files.push(conflict);
  }
  conflict_files.value = [];
}
function replace_all() {
  for (const conflict of conflict_files.value) {
    conflict.conflict_handle = 'rename';
    state.upload_files.push(conflict);
  }
  conflict_files.value = [];
}

async function upload_all_files() {
  if (state.uploading) {
    return;
  }
  state.uploading = true;
  // initialize progress for the batch
  state.upload_progress = 0;

  var counter = 1;
  let file_list = [];
  for (let file of upload_queue.value) {
    if (!file.conflict_handle) {
      file_list.push(file.name);
    }
  }
  let conflict_list = [];
  if (file_list.length > 0) {
    const resp = await $http(
      'POST',
      '/upload/check_conflicts',
      JSON.stringify({ file_names: file_list }),
      20000,
      true
    );
    conflict_list = JSON.parse(resp);
  }

  if (conflict_list.length > 0 || conflict_files.value.length > 0) {
    conflict.value = true;
  } else {
    conflict.value = false;
  }
  while (upload_queue.value.length > 0) {
    // Check for cancellation between files
    if (state.upload_cancel_requested) {
      console.log('Upload batch cancelled by user');
      break;
    }

    const to_upload = upload_queue.value.shift();
    if (!conflict_list.includes(to_upload.name)) {
      await upload_file(to_upload);
      counter += 1;
      state.upload_current_index = counter;
    } else {
      conflict_files.value.push(to_upload);
    }
  }
  // Batch complete: clear uploading flags and progress
  resetUploadState();
}

async function upload_file(file) {
  state.upload_current_filename = file.name;
  let start = 0;
  let chunk = null;
  let end = 10000000;
  let size = file.size;
  const file_name = file.name;
  // Kick off thumbnail generation in parallel with the main upload. The
  // browser is mostly idle on its decode/scale path during a large upload,
  // and we want both done by the time the multipart PUTs finish so we can
  // commit the thumbnail without blocking. Cloud-mode only — on-prem already
  // has its own background thumbnailer.
  const thumbnailPromise = IS_CLOUD_MODE
    ? generateThumbnail(file).catch((e) => {
        console.warn('[upload] thumbnail generation failed for', file.name, e);
        return null;
      })
    : Promise.resolve(null);
  try {
    let resp = undefined;

    // Check if this is a variant upload
    const isVariantUpload = !!file.variant_parent_uid;

    if (isVariantUpload) {
      // Initialize variant upload
      resp = await $http(
        'POST',
        '/upload/initialize_variant_upload?name=' +
          encodeURIComponent(file_name) +
          '&size=' +
          size +
          '&parent_asset_uid=' +
          file.variant_parent_uid,
        null,
        20000,
        true
      );
    } else if (file.conflict_handle) {
      resp = await $http(
        'POST',
        '/upload/initialize_upload?name=' +
          encodeURIComponent(file_name) +
          '&v2=true' +
          '&size=' +
          size +
          '&conflict=' +
          file.conflict_handle,
        null,
        20000,
        true
      );
    } else {
      // Build URL with optional group_uid parameter
      let uploadUrl =
        '/upload/initialize_upload?name=' +
        encodeURIComponent(file_name) +
        '&v2=true' +
        '&size=' +
        size;

      // Add group_uid if we're currently in a group context
      if (state.current_asset_group) {
        uploadUrl += '&group_uid=' + state.current_asset_group;
      }

      // Add src_uid for template upgrades
      if (file.src_uid) {
        uploadUrl += '&src_uid=' + file.src_uid;
      }

      resp = await $http('POST', uploadUrl, null, 20000, true);
    }

    let uploadedBytes = 0;

    const data = JSON.parse(resp);
    const uid = data['session_uuid'];

    // Store session UID for cancellation
    state.upload_session_uid = uid;

    // Initialize upload timing for ETA calculation
    state.upload_start_time = Date.now();
    state.upload_bytes_total = file.size;
    state.upload_bytes_done = 0;
    state.upload_eta = '';

    async function uploadWithStreaming() {
      const isChrome = !!window.chrome;
      if (!isChrome) {
        await fallbackToBasicUpload();
        return;
      }

      const CHUNK_SIZE = 90 * 1024 * 1024; // 90MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      try {
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          // Check for cancellation before each chunk
          if (state.upload_cancel_requested) {
            console.log('Upload cancelled by user');
            return;
          }

          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunkBlob = file.slice(start, end);
          const chunkSize = end - start;

          // Create transform stream for progress tracking
          let chunkUploadedBytes = 0;
          const transformStream = new TransformStream({
            async transform(chunk, controller) {
              chunkUploadedBytes += chunk.byteLength;
              uploadedBytes = start + chunkUploadedBytes;
              state.upload_progress = Math.min(
                (uploadedBytes / file.size) * 100,
                100
              );
              updateUploadETA(uploadedBytes, file.size);
              controller.enqueue(chunk);
            },
          });

          const readableStream = chunkBlob
            .stream()
            .pipeThrough(transformStream);

          // Use variant endpoint if this is a variant upload
          const uploadEndpoint = file.variant_parent_uid
            ? `/upload/upload_variant_file?uid=${uid}`
            : `/upload/upload_file?uid=${uid}&src_uid=${file.src_uid}`;

          const response = await fetch(state.devRedirect + uploadEndpoint, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/octet-stream',
              'X-Filename': file.name,
              'X-File-Size': chunkSize,
              'X-Chunk-Index': chunkIndex,
              'X-Total-Chunks': totalChunks,
            },
            body: readableStream,
            duplex: 'half',
          });

          if (!response.ok) {
            throw new Error(
              `Chunk ${chunkIndex} upload failed: ${response.statusText}`
            );
          }

          const result = await response.json();
          console.log(
            `Uploaded chunk ${result.chunk_index + 1}/${result.total_chunks} (${chunkSize} bytes)`
          );
        }

        console.log('Upload with streaming succeeded');
      } catch (error) {
        console.error(
          'Upload with streaming failed, falling back to basic upload',
          error
        );
        await fallbackToBasicUpload();
      }
    }

    async function fallbackToBasicUpload() {
      // Use smaller chunks for HTTP/1 for better progress granularity
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      try {
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          // Check for cancellation before each chunk
          if (state.upload_cancel_requested) {
            console.log('Upload cancelled by user');
            return;
          }

          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunkBlob = file.slice(start, end);
          const chunkSize = end - start;

          // Track if we got any progress events
          let gotProgressEvent = false;

          // Use XMLHttpRequest for progress tracking
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            // Send raw chunk blob as request body so Content-Length equals chunk size

            // Track upload progress for this chunk (fine-grained)
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                gotProgressEvent = true;
                // Calculate progress within this chunk
                const chunkProgress = (e.loaded / e.total) * chunkSize;
                uploadedBytes = start + chunkProgress;
                state.upload_progress = Math.min(
                  (uploadedBytes / file.size) * 100,
                  100
                );
                updateUploadETA(uploadedBytes, file.size);
              }
            });

            // Fallback: Update progress based on chunk completion
            // This ensures progress updates even if xhr.upload.progress doesn't fire
            const progressInterval = setInterval(() => {
              if (!gotProgressEvent) {
                // If no progress events, show progress based on chunks completed
                // Use a weighted approach: completed chunks + estimate for current chunk
                const completedProgress = (chunkIndex / totalChunks) * 100;
                const currentChunkProgress = (1 / totalChunks) * 50; // Assume current chunk is 50% done
                const totalProgress = Math.min(
                  completedProgress + currentChunkProgress,
                  100
                );
                state.upload_progress = totalProgress;
              }
            }, 200);

            xhr.addEventListener('load', () => {
              clearInterval(progressInterval);

              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const result = JSON.parse(xhr.responseText);

                  // Update to exact progress after chunk completes
                  uploadedBytes = end;
                  const exactProgress = (uploadedBytes / file.size) * 100;
                  state.upload_progress = Math.min(exactProgress, 100);

                  resolve(result);
                } catch (e) {
                  reject(new Error('Failed to parse response'));
                }
              } else {
                reject(
                  new Error(`Chunk ${chunkIndex} upload failed: ${xhr.status}`)
                );
              }
            });

            xhr.addEventListener('error', () => {
              clearInterval(progressInterval);
              reject(new Error(`Network error uploading chunk ${chunkIndex}`));
            });

            // Use variant endpoint if this is a variant upload
            const uploadEndpoint = file.variant_parent_uid
              ? `/upload/upload_variant_file_http1?uid=${uid}`
              : `/upload/upload_file_http1?uid=${uid}&src_uid=${file.src_uid}`;

            xhr.open('PUT', state.devRedirect + uploadEndpoint);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.setRequestHeader('X-Filename', file.name);
            xhr.setRequestHeader('X-File-Size', chunkSize.toString());
            xhr.setRequestHeader('X-Chunk-Index', chunkIndex.toString());
            xhr.setRequestHeader('X-Total-Chunks', totalChunks.toString());
            xhr.send(chunkBlob);
          });
        }
      } catch (error) {
        throw error;
      }
    }

    await uploadWithStreaming();

    // After the main upload finishes, await the thumbnail + probed
    // metadata (usually finished much earlier) and commit them to the
    // cloud. Failure here is non-fatal: the asset is already saved.
    if (IS_CLOUD_MODE) {
      const thumb = await thumbnailPromise;
      if (thumb && thumb.blob) {
        const qs = new URLSearchParams({
          uid,
          w: String(thumb.width || 0),
          h: String(thumb.height || 0),
          codec: thumb.codec || '',
        });
        try {
          await fetch(
            state.devRedirect + '/upload/thumbnail?' + qs.toString(),
            { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: thumb.blob }
          );
        } catch (e) {
          console.warn('[upload] thumbnail PUT failed:', e);
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
}
</script>
